import { BookingData } from "@src/BookingTools/App/AppDataProvider/types";
import { TierPricingType } from "../../../../../GeneratedGraphQL";
import { Maybe } from "@src/shared/node/GeneratedDatabaseTypes";

export type BookingPrice = {
  label?: Maybe<string>;
  price: number;
  quantity: number;
};

/**
 * Returns an array of booking prices to be displayed in the progress tracker.
 *
 * These prices are calculated on the front-end only, and are not verified by
 * the back end, but when enough booking data has been selected to produce the
 * verified total (once reservation has been created), the price summary also
 * shows the verified payment data, so this isn't a problem. These prices are
 * just to keep track of the estimated running cost, based on the user's
 * selections so far.
 */
export function getBookingPrices(bookingData: BookingData): BookingPrice[] {
  const { duration, attendees, selectedBookings, service, pricePoints, tier } =
    bookingData;

  if (!service) return [];

  // Can't be sure of any price for multitier services until price points are
  // selected because we need to know quantity.
  if (
    tier &&
    tier.pricingType === TierPricingType.Multiple &&
    pricePoints?.length
  )
    return pricePoints.reduce<BookingPrice[]>(
      (acc, { pricePoint, quantity }) => {
        const existingEntry = acc.find(
          ({ price }) => price === pricePoint.pricePerQuantity,
        );
        if (existingEntry) {
          existingEntry.quantity += quantity;
        } else {
          acc.push({
            label: pricePoint.name,
            price: pricePoint.pricePerQuantity,
            quantity,
          });
        }
        return acc;
      },
      [],
    );

  // Get price for multitier services with a single price
  if (tier && tier.pricingType === TierPricingType.Single && attendees) {
    return [
      {
        label: tier.name,
        price: tier.pricePoints[0].pricePerQuantity,
        quantity: attendees,
      },
    ];
  }

  // Standard bookings need attendees and duration to display a price
  if (!service.tiers?.length && attendees && duration) {
    const basePrice = duration.price ?? 0;

    // Recurring bookings or standard bookings onto an existing booking can
    // override duration price
    if (selectedBookings) {
      return selectedBookings.reduce<BookingPrice[]>((acc, booking) => {
        const existingEntry = acc.find(
          ({ price }) => price === booking.price ?? basePrice,
        );
        if (existingEntry) {
          existingEntry.quantity += attendees;
        } else {
          acc.push({ price: booking.price ?? basePrice, quantity: attendees });
        }
        return acc;
      }, []);
    }

    return [{ price: basePrice, quantity: attendees }];
  }

  // In all other scenarios, we don't have enough information to determine price
  return [];
}
